import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { NavLink, useLocation } from 'react-router-dom';
import {
	AppBar, Box, Button, Divider, Drawer, IconButton, List,
	ListItemButton, ListItemText, Menu, MenuItem, Toolbar, Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import './Header.css';
import { canAccess } from '../models/AccessControl';
import { getUserRoleNames } from '../models/User';
import { userStore } from '../stores/UserStore';
import { DropdownLink, dropdownLinks } from '../models/MenuItems';
import { departmentStore } from '../stores/DepartmentStore';
import { Department } from '../models/Department';

type AccessibleMenu = {
	menu: DropdownLink;
	items: DropdownLink['items'];
};

const canAccessMenuDepartment = (department: Department | undefined, departmentCodes?: string[]) =>
	!departmentCodes?.length
	|| Boolean(department && departmentCodes.some(code => code.toLowerCase() === department.code.toLowerCase()));

const getAccessibleMenus = (userRoles: string[], activeDepartment?: Department): AccessibleMenu[] => {
	return dropdownLinks
		.map(menu => ({
			menu,
			items: menu.items.filter(item =>
				canAccess(userRoles, item.roles ?? [])
				&& canAccessMenuDepartment(activeDepartment, item.departmentCodes)),
		}))
		.filter(({ items }) => items.length > 0);
};

const Header = () => {
	const { pathname } = useLocation();
	const userName = userStore.currentUser?.name;
	const roles = getUserRoleNames(userStore.currentUser);
	const [isMobileOpen, setIsMobileOpen] = useState(false);
	const [menuAnchors, setMenuAnchors] = useState<Record<string, HTMLElement | null>>({});

	const env = process.env.NODE_ENV === 'development' ? ' (Dev)' : '';
	const hasRoles = roles.length > 0;
	const allowedDepartments = departmentStore.allowedDepartments;
	const activeDepartment = departmentStore.activeDepartment;
	const accessibleMenus = getAccessibleMenus(roles, activeDepartment);
	const canUseApplication = Boolean(userStore.currentUser?.isActive && hasRoles && activeDepartment);
	const canSwitchDepartment = allowedDepartments.length > 1;
	const brandTitle = activeDepartment ? `${activeDepartment.name} Quick Finances${env}` : `Quick Finances${env}`;

	const openMenu = (menuId: string, anchor: HTMLElement) => {
		setMenuAnchors(prev => ({ ...prev, [menuId]: anchor }));
	};

	const closeMenu = (menuId: string) => {
		setMenuAnchors(prev => ({ ...prev, [menuId]: null }));
	};

	const closeMobileDrawer = () => {
		setIsMobileOpen(false);
	};

	const switchDepartment = (departmentId: number) => {
		departmentStore.switchDepartment(departmentId);
		closeMenu('department');
		closeMobileDrawer();
	};

	const isPathActive = (to: string) => pathname === to || pathname.startsWith(`${to}/`);
	const isMenuActive = (items: DropdownLink['items']) => items.some(item => isPathActive(item.to));

	return (
		<AppBar position='sticky' className='header' elevation={2}>
			<Toolbar className='header-toolbar'>
				{canUseApplication && (
					<IconButton
						color='inherit'
						edge='start'
						className='header-mobile-toggle'
						onClick={() => setIsMobileOpen(true)}
					>
						<MenuIcon />
					</IconButton>
				)}

				<Box className='header-brand-wrap'>
					{canUseApplication ? (
						<Button
							color='inherit'
							className='header-brand-button'
							endIcon={canSwitchDepartment ? <ExpandMoreIcon fontSize='small' /> : undefined}
							onClick={(event) => canSwitchDepartment && openMenu('department', event.currentTarget)}
						>
							{brandTitle}
						</Button>
					) : (
						<Typography
							component={NavLink}
							to='/'
							variant='h6'
							className='header-brand'
						>
							Quick Finances{env}
						</Typography>
					)}
					{canUseApplication && canSwitchDepartment && (
						<Menu
							anchorEl={menuAnchors.department ?? null}
							open={Boolean(menuAnchors.department)}
							onClose={() => closeMenu('department')}
						>
							{allowedDepartments.map(department => (
								<MenuItem
									key={department.id}
									selected={department.id === activeDepartment?.id}
									className='header-submenu-item'
									onClick={() => switchDepartment(department.id)}
								>
									{department.name} Quick Finances
								</MenuItem>
							))}
						</Menu>
					)}
				</Box>

				{canUseApplication && (
					<Box className='header-desktop-nav'>
						{accessibleMenus.map(({ menu, items }) => {
							const activeMenu = isMenuActive(items);

							if (items.length === 1) {
								return (
									<Button
										key={menu.id}
										component={NavLink}
										to={items[0].to}
										color='inherit'
										className={`header-nav-button ${activeMenu ? 'header-nav-button-active' : ''}`}
									>
										{menu.title}
									</Button>
								);
							}

							const anchorEl = menuAnchors[menu.id] ?? null;

							return (
								<Box key={menu.id}>
									<Button
										color='inherit'
										className={`header-nav-button ${activeMenu ? 'header-nav-button-active' : ''}`}
										endIcon={<ExpandMoreIcon fontSize='small' />}
										onClick={(event) => openMenu(menu.id, event.currentTarget)}
									>
										{menu.title}
									</Button>
									<Menu
										anchorEl={anchorEl}
										open={Boolean(anchorEl)}
										onClose={() => closeMenu(menu.id)}
									>
										{items.map(item => (
											<MenuItem
												key={item.to}
												component={NavLink}
												to={item.to}
												selected={isPathActive(item.to)}
												className='header-submenu-item'
												onClick={() => closeMenu(menu.id)}
											>
												{item.label}
											</MenuItem>
										))}
									</Menu>
								</Box>
							);
						})}
					</Box>
				)}

				{hasRoles && (
					<Button
						component={NavLink}
						to='/logout'
						color='inherit'
						className='header-logout'
					>
						{userName}
					</Button>
				)}
			</Toolbar>

			<Drawer anchor='left' open={isMobileOpen} onClose={closeMobileDrawer}>
				<Box className='header-drawer' role='presentation' onClick={closeMobileDrawer}>
					{canUseApplication && (
						<List>
							{allowedDepartments.length > 0 && (
								<Box>
									<ListItemText
										primary='Department'
										slotProps={{ primary: { className: 'header-drawer-section-title' } }}
									/>
									{allowedDepartments.map(department => (
										<ListItemButton
											key={department.id}
											selected={department.id === activeDepartment?.id}
											className='header-drawer-link'
											onClick={() => switchDepartment(department.id)}
										>
											<ListItemText primary={department.name} />
										</ListItemButton>
									))}
								</Box>
							)}
							{accessibleMenus.map(({ menu, items }) => (
								<Box key={menu.id}>
									<ListItemText
										primary={menu.title}
										slotProps={{ primary: { className: 'header-drawer-section-title' } }}
									/>
									{items.map(item => (
										<ListItemButton
											key={item.to}
											component={NavLink}
											to={item.to}
											selected={isPathActive(item.to)}
											className='header-drawer-link'
										>
											<ListItemText primary={item.label} />
										</ListItemButton>
									))}
								</Box>
							))}
						</List>
					)}

					{hasRoles && (
						<>
							<Divider />
							<List>
								<ListItemButton component={NavLink} to='/logout'>
									<ListItemText primary={`Sign out (${userName ?? 'User'})`} />
								</ListItemButton>
							</List>
						</>
					)}
				</Box>
			</Drawer>
		</AppBar>
	);
};

export default observer(Header);
